import React, { useEffect, useState } from "react";
import { Box, Typography, Stack, Popover } from "@mui/material";
import moment from "moment";

import TextField from "@mui/material/TextField";
import { styled } from "@mui/material/styles";

import InputMask from "react-input-mask";
import { DateRangePicker as RangePicker } from "react-date-range";
import { subDays } from "date-fns";
import "react-date-range/dist/styles.css";
import "react-date-range/dist/theme/default.css";
import "../../../styles/analytics/date-range.scss";

const BootstrapInput = styled(TextField)(({ theme }) => ({
  "& .MuiInputBase-input": {
    borderRadius: 5,
    position: "relative",
    fontSize: "14px !important",
    padding: "10px !important",
    width: "75px",
  },
}));

const DateRangePicker = ({
  range,
  setRange,
  period,
  setPeriod,
  periodList,
  title,
  calendarClass = "",
  disableRangeSelection,
  disableCalendar,
  isValidRange,
  setIsValidRange,
}) => {
  const [anchorEl, setAnchorEl] = useState(null);
  const open = Boolean(anchorEl);
  const [startDateString, setStartDateString] = useState("");
  const [endDateString, setEndDateString] = useState("");
  const [isValidStartDate, setIsValidStartDate] = useState(true);
  const [isValidEndDate, setIsValidEndDate] = useState(true);

  useEffect(() => {
    if (!isValidStartDate || !isValidEndDate) {
      setIsValidRange(false);
    } else {
      setIsValidRange(true);
    }
  }, [isValidStartDate, isValidEndDate]);

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleOpen = (event) => {
    setAnchorEl(event.currentTarget);
  };

  useEffect(() => {
    if (range?.startDate) {
      setStartDateString(moment(range.startDate).format("MM/DD/YYYY"));
    }
  }, [range?.startDate]);

  useEffect(() => {
    if (
      isValidDate(startDateString) &&
      moment(range.startDate).format("MM/DD/YYYY") !== startDateString &&
      moment(startDateString, "MM/DD/YYYY").diff(
        moment(range.endDate),
        "days"
      ) < 0 &&
      moment(startDateString, "MM/DD/YYYY").diff(moment(), "days") < 0 &&
      isValidDate(endDateString) &&
      moment(endDateString, "MM/DD/YYYY").diff(
        moment(range.startDate),
        "days"
      ) > 0 &&
      moment(endDateString, "MM/DD/YYYY").diff(moment(), "days") < 0
    ) {
      setRange({ ...range, startDate: new Date(Date.parse(startDateString)) });
    }
  }, [startDateString]);

  useEffect(() => {
    if (range?.endDate) {
      setEndDateString(moment(range.endDate).format("MM/DD/YYYY"));
    }
  }, [range?.endDate]);

  useEffect(() => {
    if (
      isValidDate(endDateString) &&
      moment(range.endDate).format("MM/DD/YYYY") !== endDateString &&
      moment(endDateString, "MM/DD/YYYY").diff(
        moment(range.startDate),
        "days"
      ) > 0 &&
      moment(endDateString, "MM/DD/YYYY").diff(moment(), "days") < 0 &&
      isValidDate(startDateString) &&
      moment(startDateString, "MM/DD/YYYY").diff(
        moment(range.endDate),
        "days"
      ) < 0 &&
      moment(startDateString, "MM/DD/YYYY").diff(moment(), "days") < 0
    ) {
      setRange({ ...range, endDate: new Date(Date.parse(endDateString)) });
    }
  }, [endDateString]);

  const onChangeStartDateString = (value) => {
    if (
      !isValidDate(value) ||
      !(
        moment(value, "MM/DD/YYYY").diff(moment(range.endDate), "days") < 0 &&
        moment(value, "MM/DD/YYYY").diff(moment(), "days") < 0
      )
    ) {
      setIsValidStartDate(false);
    } else {
      setIsValidStartDate(true);
    }
    setPeriod("custom");
    setStartDateString(value);
  };

  const onChangeEndDateString = (value) => {
    if (
      !isValidDate(value) ||
      !(
        moment(value, "MM/DD/YYYY").diff(moment(range.startDate), "days") > 0 &&
        moment(value, "MM/DD/YYYY").diff(moment(), "days") < 0
      )
    ) {
      setIsValidEndDate(false);
    } else {
      setIsValidEndDate(true);
    }
    setPeriod("custom");
    setEndDateString(value);
  };

  const isValidDate = (date) => {
    var regDate =
      /^((0?[1-9]|1[012])[- /.](0?[1-9]|[12][0-9]|3[01])[- /.](19|20)?[0-9]{2})*$/;
    return regDate.test(date);
  };

  return (
    <Box className="date-inputs-container">
      <Box sx={{ marginBottom: "10px" }}>
        <Stack
          direction="row"
          spacing="5px"
          alignItems="center"
          className="date-label"
        >
          <Typography component="span">{title.label}</Typography>
          <Box
            className="current-period-mark"
            sx={{ backgroundColor: title.markColor }}
          />
        </Stack>

        <Box className="custom-select" onClick={handleOpen}>
          <Box>{periodList.find((x) => x.value === period).label}</Box>
          <i className={`fa fa-caret-down`} aria-hidden="true"></i>
        </Box>

        <Popover
          open={open}
          anchorEl={anchorEl}
          onClose={handleClose}
          anchorOrigin={{
            vertical: "top",
            horizontal: "left",
          }}
          transformOrigin={{
            vertical: "top",
            horizontal: "left",
          }}
          PaperProps={{
            sx: {
              boxShadow: 0,
              borderRadius: "5px",
              border: "solid 1px #9CA3AF",
            },
          }}
          transitionDuration={0}
        >
          <Stack
            className="custom-select-popover"
            sx={{ width: "230px", padding: "0px" }}
          >
            {[
              periodList.find((x) => x.value === period),
              ...periodList.filter((x) => x.value !== period),
            ].map((period, index) => (
              <Stack
                key={period.value}
                direction="row"
                justifyContent="space-between"
                alignItems="center"
                className="select-item"
                onClick={() => {
                  setPeriod(period.value);
                  handleClose();
                }}
              >
                <Box>{period.label}</Box>
                {index === 0 && (
                  <i className={`fa fa-caret-up`} aria-hidden="true"></i>
                )}
              </Stack>
            ))}
          </Stack>
        </Popover>

        <Stack
          direction="row"
          alignItems="center"
          justifyContent="space-between"
          className={`date-input ${disableCalendar ? "disabled-calendar" : ""}`}
        >
          <InputMask
            mask="99/99/9999"
            value={startDateString}
            onChange={(value) => onChangeStartDateString(value.target.value)}
          >
            <BootstrapInput error={!isValidStartDate} />
          </InputMask>

          <i class="fa fa-arrow-right" aria-hidden="true"></i>

          <InputMask
            mask="99/99/9999"
            value={endDateString}
            onChange={(value) => onChangeEndDateString(value.target.value)}
          >
            <BootstrapInput error={!isValidEndDate} />
          </InputMask>
        </Stack>
      </Box>
      <RangePicker
        ranges={[range]}
        onChange={(ranges) => {
          ranges.selection.endDate?.setHours(23, 59, 59, 999);
          if (
            moment(ranges.selection.startDate).diff(
              moment(ranges.selection.endDate),
              "days"
            ) === 0 &&
            period !== "custom"
          ) {
            setPeriod("custom");
          }
          setRange(ranges.selection);
        }}
        className={`${
          disableRangeSelection ? "disabled-range-selection" : ""
        } ${calendarClass} ${disableCalendar ? "disabled-calendar" : ""}`}
        showSelectionPreview={false}
        editableDateInputs={false}
        moveRangeOnFirstSelection={false}
        weekdayDisplayFormat="EEEEEE"
        weekStartsOn={1}
        maxDate={subDays(new Date(), 1)}
        navigatorRenderer={(focusedDate, changeShownDate, props) => {
          const { showMonthArrow, locale, showMonthAndYearPickers } = props;

          return (
            <Stack
              direction="row"
              justifyContent="space-between"
              alignItems="center"
              sx={{ marginBottom: "5px" }}
              onMouseUp={(e) => e.stopPropagation()}
            >
              {showMonthAndYearPickers && (
                <Typography
                  component="span"
                  sx={{ color: "#4A4D52", fontSize: "16px", fontWeight: 500 }}
                >
                  {locale.localize.month(focusedDate.getMonth())}{" "}
                  {focusedDate.getFullYear()}
                </Typography>
              )}

              {showMonthArrow ? (
                <Stack
                  direction="row"
                  spacing="15px"
                  className="month-arrow-container"
                >
                  <i
                    class="fa fa-chevron-left"
                    aria-hidden="true"
                    onClick={() => changeShownDate(-1, "monthOffset")}
                  ></i>
                  <i
                    class="fa fa-chevron-right"
                    aria-hidden="true"
                    onClick={() => changeShownDate(+1, "monthOffset")}
                  ></i>
                </Stack>
              ) : null}
            </Stack>
          );
        }}
      />
    </Box>
  );
};

export default DateRangePicker;
